/**
 * Performance, GPS anomalies and sync issues copy (Arabic). `dashboard`,
 * `anomalies`, `sync` and `fields` are carried verbatim from the previous app.
 * One key is new: `anomalies.superAdminOnly` — the previous page read a key it
 * never defined (`settings.superAdminOnly`); its wording is the previous
 * app's own "only a super admin can change them" sentence.
 */
export const analyticsAr = {
  dashboard: {
    title: 'الأداء',
    description: 'الزيارات التي سجلها المندوبون ونتيجة مزامنتها مع Zoho Desk.',
    visits: 'الزيارات',
    synced: 'تمت مزامنتها مع Zoho',
    partiallySynced: 'مزامنة جزئية',
    failed: 'فشلت',
    inProgress: 'قيد المعالجة',
    anomalies: 'مخالفات',
    avgDuration: 'متوسط المدة',
    syncRate: 'نجاح المزامنة',
    anomalyRate: 'نسبة المخالفات',
    byAgent: 'حسب المندوب',
    byBranch: 'حسب الفرع',
    byDay: 'الزيارات يومياً',
    allAgents: 'كل المندوبين',
    statusFailed: 'زيارات فشلت في المزامنة في هذه الفترة',
    statusPartial: 'زيارات تمت مزامنتها جزئياً في هذه الفترة',
    statusHealthy: 'المزامنة تعمل — لا توجد زيارات فاشلة في هذه الفترة',
    totalHint: 'الفترة المحددة',
    anomalyFilterHint: 'يفلتر جدولي المندوبين والفروع',
    anomalyFilterOn: 'الصفوف التي بها مخالفات فقط',
    issues: {
      title: 'المشاكل المسجلة',
      hint: 'تُحسب لكل زيارة مشروع',
      projectVisits: 'زيارات المشاريع',
      withIssue: 'بها مشكلة',
      rate: 'نسبة المشاكل',
      count: 'مرات التسجيل',
      byService: 'حسب الخدمة',
      byIssueType: 'حسب نوع المشكلة',
      byIssue: 'حسب المشكلة',
      byBranch: 'المشاكل حسب الفرع',
      byDay: 'المشاكل يومياً',
    },
  },
  anomalies: {
    // New copy (UX audit #11): needs native review.
    emptyFiltered: 'لا توجد مخالفات تطابق عوامل التصفية.',
    emptyNone: 'لا توجد مخالفات في هذه الفترة.',
    title: 'مخالفات الموقع',
    description: 'زيارات وصلت فحوص موقعها وتوقيتها إلى حد درجة المخالفة.',
    statusCount: 'مخالفة في هذه الفترة',
    statusCount_one: 'مخالفة في هذه الفترة',
    statusCount_two: 'مخالفتين في هذه الفترة',
    statusCount_few: 'مخالفات في هذه الفترة',
    statusNone: 'لا توجد مخالفات في هذه الفترة',
    score: 'الدرجة',
    minScore: 'أقل درجة',
    // New copy (UX audit #21): needs native review.
    minScorePlaceholder: 'مخالفة من درجة {{threshold}}',
    reasons: 'الأسباب',
    distanceEnd: 'المسافة عند الانصراف',
    ticket: 'التذكرة',
    map: 'خريطة',
    table: 'جدول',
    view: 'العرض',
    agentLocation: 'موقع المندوب',
    branchLocation: 'موقع الفرع',
    detailTitle: 'مخالفة زيارة',
    thresholds: 'حدود الاكتشاف',
    maxAccuracy: 'تجاهل قراءات GPS الأقل دقة من (م)',
    minDuration: 'أقل مدة للزيارة (دقيقة)',
    maxDuration: 'أقصى مدة للزيارة (دقيقة)',
    maxClockSkew: 'أقصى فرق في ساعة الجهاز (دقيقة)',
    scoreThreshold: 'اعتبارها مخالفة من درجة',
    weights: 'وزن كل سبب',
    superAdminOnly: 'التعديل للمدير العام بس.',
    reason: {
      START_OUTSIDE_GEOFENCE: 'الحضور خارج نطاق الفرع',
      END_OUTSIDE_GEOFENCE: 'الانصراف خارج نطاق الفرع',
      LOW_ACCURACY: 'دقة GPS ضعيفة',
      MOCK_LOCATION: 'تطبيق موقع وهمي',
      LOCATION_MISSING: 'الموقع غير متاح',
      BRANCH_LOCATION_UNVERIFIED: 'موقع الفرع غير مؤكد',
      UNLISTED_BRANCH: 'فرع غير مسجل',
      DURATION_TOO_SHORT: 'زيارة أقصر من الحد',
      DURATION_TOO_LONG: 'زيارة أطول من الحد',
      CLOCK_SKEW: 'ساعة الجهاز مختلفة عن الخادم',
      OVERLAPPING_VISIT: 'تتداخل مع زيارة أخرى',
    },
  },
  sync: {
    // New copy (UX audit #26): needs native review.
    openVisit: 'فتح الزيارة',
    // New copy (UX audit #11): needs native review.
    emptyFiltered: 'لا توجد مشاكل مزامنة تطابق عامل التصفية.',
    title: 'مشاكل المزامنة مع Zoho',
    description: 'زيارات لم يتمكن عامل المزامنة من توصيلها بالكامل إلى Zoho Desk.',
    statusCount: 'زيارة تحتاج مراجعة',
    statusCount_one: 'زيارة تحتاج مراجعة',
    statusCount_two: 'زيارتين تحتاجان مراجعة',
    statusCount_few: 'زيارات تحتاج مراجعة',
    statusNone: 'لا توجد مشاكل مزامنة',
    filter: 'الحالة',
    attempts: 'المحاولات',
    lastError: 'آخر خطأ',
    retry: 'إعادة المزامنة',
    retried: 'تمت إعادة مزامنة الزيارة {{visit}}',
    failedPhotos: 'صور فشل رفعها',
    pendingUploads: 'صور لم يرفعها الجهاز',
    photos: 'الصور',
    photosUploaded: 'تم رفع {{uploaded}} من {{total}} صور',
    detailTitle: 'تفاصيل المزامنة',
    workerLog: 'خطأ عامل المزامنة',
    noError: 'لا يوجد خطأ مسجل.',
  },
  /** Field names in the detail dialogs: never the raw API key. */
  fields: {
    visitId: 'رقم الزيارة',
    submissionId: 'رقم الإرسال',
    status: 'الحالة',
    zohoTicketNumber: 'تذكرة Zoho',
    syncAttempts: 'محاولات المزامنة',
    receivedAt: 'وقت الاستلام',
    failedAttachments: 'صور فشل رفعها',
    pendingAttachmentUploads: 'صور لسه على الموبايل',
    anomalyScore: 'درجة المخالفة',
    agent: 'المندوب',
    brandName: 'التاجر',
    branchName: 'الفرع',
    startedAt: 'الحضور',
    endedAt: 'الانصراف',
    durationMinutes: 'المدة (دقيقة)',
    startLocation: 'موقع الحضور',
    endLocation: 'موقع الانصراف',
    branchLocation: 'موقع الفرع',
    distanceStartMeters: 'المسافة عند الحضور (م)',
    distanceEndMeters: 'المسافة عند الانصراف (م)',
    reasonCode: 'السبب',
    weight: 'الوزن',
    detail: 'التفاصيل',
  },
};
